import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, Avatar, Button } from "@material-ui/core";
import { Trash2, Upload, User } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { i18n } from "../../translate/i18n";
import StandardModal from "../ui/StandardModal";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      borderRadius: 8,
    },
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 144,
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
    cursor: "pointer",
    margin: theme.spacing(2, 0),
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    padding: theme.spacing(2),
  },
  dropzoneActive: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light,
  },
  preview: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: theme.spacing(2, 0),
  },
  avatar: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    margin: theme.spacing(1),
    boxShadow: theme.shadows[5],
    border: `4px solid ${theme.palette.background.paper}`,
  },
  instructions: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  dropzoneText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  iconText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "& svg": {
      fontSize: 40,
      marginBottom: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  },
  uploadButton: {
    marginTop: theme.spacing(2),
    borderRadius: 8,
  },
  deleteButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
}));

const AvatarModal = ({ open, onClose, onSave, currentAvatar, onDelete }) => {
  const classes = useStyles();
  const [file, setFile] = useState(null);
  const [showDropzone, setShowDropzone] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    },
  });

  const handleSave = async () => {
    if (file) {
      await onSave(file);
    }
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    setFile(null);
    setShowDropzone(true);
  };

  const dropzoneClasses = `${classes.dropzone} ${
    isDragActive ? classes.dropzoneActive : ""
  }`;

  const handleCancelClick = () => {
    if (showDropzone && !file) {
      setShowDropzone(false);
    } else {
      onClose();
    }
  };

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={i18n.t("userModal.avatarModal.title")}
      maxWidth="xs"
      primaryButton={{
        text: i18n.t("userModal.avatarModal.save"),
        color: "primary",
        onClick: handleSave,
        visible: true,
        disabled: !file && !currentAvatar,
      }}
      secondaryButton={{
        text: i18n.t("userModal.avatarModal.cancel"),
        color: "secondary",
        onClick: handleCancelClick,
        visible: true,
      }}
      tertiaryButton={{
        text: i18n.t("userModal.avatarModal.remove"),
        color: "default",
        onClick: handleDelete,
        visible: !!(currentAvatar || file),
        disabled: false,
        startIcon: <Trash2 size={20} />,
        align: "left",
        className: classes.deleteButton,
      }}
    >
      {currentAvatar && !file && !showDropzone && (
        <Box className={classes.preview}>
          <Avatar src={currentAvatar} className={classes.avatar}>
            <User size={24} />
          </Avatar>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("userModal.avatarModal.current")}
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowDropzone(true)}
            className={classes.uploadButton}
            startIcon={<Upload size={20} />}
          >
            {i18n.t("userModal.avatarModal.changePhoto")}
          </Button>
        </Box>
      )}

      {file && (
        <Box className={classes.preview}>
          <Avatar src={URL.createObjectURL(file)} className={classes.avatar}>
            <User size={24} />
          </Avatar>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("userModal.avatarModal.preview")}
          </Typography>
        </Box>
      )}

      {(!currentAvatar || showDropzone) && !file && (
        <div {...getRootProps({ className: dropzoneClasses })}>
          <input {...getInputProps()} />
          <div className={classes.iconText}>
            <Upload size={24} />
            <Typography variant="body2" className={classes.dropzoneText}>
              {isDragActive
                ? i18n.t("userModal.avatarModal.dropHere")
                : i18n.t("userModal.avatarModal.dropzoneText")}
            </Typography>
          </div>
        </div>
      )}

      {(!currentAvatar || showDropzone || file) && (
        <Typography variant="caption" className={classes.instructions}>
          {i18n.t("userModal.avatarModal.hint")}
        </Typography>
      )}
    </StandardModal>
  );
};

export default AvatarModal;
